import { Form, Formik, FormikProps } from 'formik';
import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'src/components/Dialog';
import { Stack } from 'src/components/Stack';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import * as Yup from 'yup';

interface PosPinDialogProps {
  open: boolean;
  onSubmit: (pin: string, password?: string) => void;
  onClose?: () => void;
  isSubmitting: boolean;
  error?: string;
  kioskMode?: boolean;
  kioskStaffMode?: boolean;
  setupPin?: boolean;
  paymentPin?: boolean;
}

type DialogMode = 'kioskStaff' | 'setup' | 'payment' | 'validate';

interface DialogConfig {
  title: string;
  subtitle: string;
  submitLabel: string;
  submittingLabel: string;
}

const PosPinDialog: React.FC<PosPinDialogProps> = ({
  open,
  onSubmit,
  onClose,
  kioskMode = false,
  kioskStaffMode = false,
  isSubmitting,
  setupPin = false,
  paymentPin = false,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'posPinDialog',
  });
  const { t: tCommon } = useTranslation('translation', {
    keyPrefix: 'common.buttons',
  });

  const formikRef =
    useRef<FormikProps<{ pin: string; password?: string }>>(null);

  // Determine dialog mode based on props
  const dialogMode = useMemo<DialogMode>(() => {
    if (kioskStaffMode) return 'kioskStaff';
    if (setupPin) return 'setup';
    if (paymentPin) return 'payment';
    return 'validate';
  }, [kioskStaffMode, setupPin, paymentPin]);

  // Configuration for each dialog mode
  const dialogConfig = useMemo<DialogConfig>(() => {
    const configs: Record<DialogMode, DialogConfig> = {
      kioskStaff: {
        title: t('unlockKioskTitle'),
        subtitle: t('unlockKioskDescription'),
        submitLabel: t('unlockKiosk'),
        submittingLabel: t('unlockingKiosk'),
      },
      setup: {
        title: t('setupKioskPin'),
        subtitle: kioskMode
          ? t('setupKioskPinDescription')
          : t('enterPinDescription'),
        submitLabel: t('setupPin'),
        submittingLabel: t('settingUpPin'),
      },
      payment: {
        title: t('enterPaymentPinTitle'),
        subtitle: kioskMode
          ? t('enterPaymentPinDescription')
          : t('enterPinDescription'),
        submitLabel: t('validatePin'),
        submittingLabel: t('validatingPin'),
      },
      validate: {
        title: t('enterPinTitle'),
        subtitle: kioskMode
          ? t('kioskPinDescription')
          : t('enterPinDescription'),
        submitLabel: t('validatePin'),
        submittingLabel: t('validatingPin'),
      },
    };

    return configs[dialogMode];
  }, [dialogMode, kioskMode, t]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        pin: Yup.string()
          .matches(/^\d+$/, t('pinDigitsOnly'))
          .min(4, t('pinMinDigits'))
          .max(12, t('pinMaxDigits'))
          .required(t('pinRequired')),
        ...(setupPin && {
          password: Yup.string().required(t('passwordIsRequired')),
        }),
      }),
    [setupPin, t]
  );

  const handleSubmit = async (values: { pin: string; password?: string }) => {
    onSubmit(values.pin, values.password);
  };

  // Reset form when modal opens
  useEffect(() => {
    if (open && formikRef.current) {
      formikRef.current.resetForm();
    }
  }, [open]);

  const dialogActions = useMemo(
    () => [
      ...(onClose
        ? [
            {
              label: tCommon('cancel'),
              onClick: onClose,
              variant: 'outlined' as const,
              disabled: isSubmitting,
            },
          ]
        : []),
      {
        label: isSubmitting
          ? dialogConfig.submittingLabel
          : dialogConfig.submitLabel,
        onClick: () => formikRef.current?.submitForm(),
        variant: 'contained' as const,
        disabled: isSubmitting,
      },
    ],
    [onClose, isSubmitting, dialogConfig, tCommon]
  );

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{ pin: '' }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={false}
    >
      {() => (
        <Dialog
          open={open}
          onClose={onClose}
          title={dialogConfig.title}
          subtitle={dialogConfig.subtitle}
          maxWidth="sm"
          fullWidth
          actions={dialogActions}
        >
          <Form>
            <Stack spacing={2}>
              <FormTextField
                label=""
                name="pin"
                disabled={isSubmitting}
                placeholder={t('enterPinPlaceholder')}
                type="password"
                InputProps={{
                  inputProps: {
                    maxLength: 12,
                    pattern: '[0-9]*',
                  },
                }}
              />
              {setupPin && (
                <FormTextField
                  label=""
                  name="password"
                  type="password"
                  disabled={isSubmitting}
                  placeholder={t('enterPasswordPlaceholder')}
                />
              )}
            </Stack>
          </Form>
        </Dialog>
      )}
    </Formik>
  );
};

export default PosPinDialog;
